import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getEntregasArmado } from '../../lib/entregasApi'
import { labelVentana } from '../../lib/ventanasHorarias'

const VIVIENDA = { casa: 'CASA', edificio: 'EDIFICIO', oficina: 'OFICINA' }

export default function Armado() {
  const [params] = useSearchParams()
  const ids = (params.get('ids') || '').split(',').filter(Boolean)
  const [entregas, setEntregas] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (ids.length === 0) { setLoading(false); return }
    getEntregasArmado(ids).then(d => { setEntregas(d); setLoading(false) })
  }, [params])

  // Resumen consolidado: cuánto de cada producto hay que sacar del depósito en total
  const consolidado = useMemo(() => {
    const m = {}
    entregas.forEach(e => {
      // composicion = caja real neteada (kit + extras, ya sin líneas "−1")
      const items = e.composicion || [...(e.kit_items || []), ...(e.extras || [])]
      items.forEach(it => {
        const k = it.sku || it.nombre
        if (!m[k]) m[k] = { sku: it.sku, nombre: it.nombre, marca: it.marca, presentacion: it.presentacion, cantidad: 0 }
        m[k].cantidad += Number(it.cantidad) || 0
      })
    })
    return Object.values(m)
      .filter(p => (Number(p.cantidad) || 0) > 0)
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
  }, [entregas])

  const fechaLabel = (f) => {
    if (!f) return ''
    const d = new Date(f + 'T12:00:00')
    return d.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase()
  }
  const ventanaLabel = (v) => labelVentana(v).toUpperCase()

  if (loading) return <div className="loading">Cargando hoja de armado...</div>

  if (entregas.length === 0) {
    return (
      <div className="page" style={{ padding: 32 }}>
        <h1>Sin pedidos</h1>
        <p>No se encontraron entregas. Volvé a /admin/entregas y seleccioná pedidos.</p>
      </div>
    )
  }

  const fecha = entregas[0]?.fecha_programada
  const totalBultos = entregas.reduce((s, e) => s + (e.bultos_total || 1), 0)

  return (
    <>
      <div className="armado-toolbar no-print">
        <div>
          <strong>{entregas.length}</strong> pedido{entregas.length !== 1 ? 's' : ''} · <strong>{totalBultos}</strong> bulto{totalBultos !== 1 ? 's' : ''}
        </div>
        <div>
          <button className="btn primary" onClick={() => window.print()}>🖨️ Imprimir</button>
          <button className="btn ghost" onClick={() => window.history.back()} style={{ marginLeft: 8 }}>← Volver</button>
        </div>
      </div>

      <div className="armado-page">

        {/* PORTADA: resumen consolidado para hacer picking del depósito */}
        <section className="ar-section">
          <header className="ar-header">
            <div>
              <div className="ar-title">HOJA DE ARMADO</div>
              <div className="ar-fecha">{fechaLabel(fecha)}</div>
            </div>
            <div className="ar-stats">
              <div><span className="ar-stat-num">{entregas.length}</span> pedidos</div>
              <div><span className="ar-stat-num">{totalBultos}</span> bultos</div>
            </div>
          </header>

          <div className="ar-consol-title">PICKING DEL DEPÓSITO — TOTAL A SACAR</div>
          <table className="ar-consol">
            <thead>
              <tr>
                <th style={{ width: 40, textAlign: 'center' }}>✓</th>
                <th style={{ width: 60, textAlign: 'right' }}>Cant.</th>
                <th>Producto</th>
                <th style={{ width: 110 }}>Presentación</th>
                <th style={{ width: 90 }}>SKU</th>
              </tr>
            </thead>
            <tbody>
              {consolidado.map((p, i) => (
                <tr key={i}>
                  <td style={{ textAlign: 'center' }}><span className="ar-check"></span></td>
                  <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 15 }}>{p.cantidad}</td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{p.nombre}</div>
                    {p.marca && <div style={{ fontSize: 10, color: '#666' }}>{p.marca}</div>}
                  </td>
                  <td style={{ fontSize: 11 }}>{p.presentacion || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 10, color: '#666' }}>{p.sku || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* DETALLE POR CLIENTE */}
        {entregas.map((e, idx) => (
          <section key={e.id} className="ar-pedido">
            <header className="ar-ped-head">
              <div className="ar-ped-num">#{idx + 1} de {entregas.length}</div>
              <div className="ar-ped-cliente">
                <div className="ar-ped-nombre">{(e.cliente?.beneficiario || e.cliente?.titular)?.nombre || '—'}</div>
                {e.cliente?.beneficiario && e.cliente?.titular && e.cliente.beneficiario.id !== e.cliente.titular.id && (
                  <div style={{ fontSize: 11, color: '#7C3AED', marginTop: 2, fontWeight: 600 }}>
                    🎁 contrata: {e.cliente.titular.nombre}
                  </div>
                )}
                <div className="ar-ped-dir">
                  <strong>{e.cliente?.barrio}</strong>
                  {e.cliente?.tipo_vivienda && <span className="ar-ped-vivienda">{VIVIENDA[e.cliente.tipo_vivienda]}</span>}
                  {' · '}
                  {e.cliente?.direccion}{e.cliente?.apto ? ` · ${e.cliente.apto}` : ''}
                </div>
              </div>
              <div className="ar-ped-meta">
                <div className="ar-ped-bultos">{e.bultos_total || 1}<span>BULTO{(e.bultos_total || 1) !== 1 ? 'S' : ''}</span></div>
                <div className="ar-ped-ventana">{ventanaLabel(e.ventana_horaria)}</div>
                <div className="ar-ped-orden">{e.numero_orden}</div>
              </div>
            </header>

            {e.notas && (
              <div className="ar-notas">
                <strong>★ NOTAS:</strong> {e.notas}
              </div>
            )}

            <div className="ar-ped-body">
              <div className="ar-ped-kit-name">
                {e.kit?.nombre ? `📦 ${e.kit.nombre}` : '— sin kit —'}
              </div>
              <table className="ar-ped-table">
                <tbody>
                  {(e.composicion || [...(e.kit_items || []), ...(e.extras || [])])
                    .filter(it => (Number(it.cantidad) || 0) > 0)
                    .map((it, i) => (
                    <tr key={'c' + i} style={it.es_regalo ? { background: '#FEF3C7' } : null}>
                      <td className="ar-c"><span className="ar-check"></span></td>
                      <td className="ar-q">{it.cantidad}</td>
                      <td className="ar-n">
                        {it.es_regalo && <span style={{ marginRight: 4, fontSize: 14 }}>🎁</span>}
                        <span className="ar-prod">{it.nombre}</span>
                        {it.presentacion && <span className="ar-pres"> · {it.presentacion}</span>}
                        {it.es_regalo && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#92400E', letterSpacing: '.06em' }}>REGALO</span>}
                      </td>
                      <td className="ar-s">{it.sku || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}

      </div>

      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          body { background: #fff; margin: 0; }
          .no-print { display: none !important; }
          .ar-section, .ar-pedido { page-break-inside: avoid; }
          .ar-section { page-break-after: always; }
        }
        @media screen {
          body { background: #E5E7EB; }
        }
        .armado-toolbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          background: var(--bg-dark, #1A3461); color: #fff;
          padding: 12px 24px; display: flex; align-items: center; justify-content: space-between;
        }
        .armado-page {
          background: #fff; max-width: 186mm; margin: 76px auto 0;
          padding: 14mm; box-shadow: 0 4px 16px rgba(0,0,0,.1);
          font-family: 'Inter', sans-serif; color: #000;
        }

        /* PORTADA */
        .ar-section { margin-bottom: 14mm }
        .ar-header {
          display: flex; justify-content: space-between; align-items: flex-end;
          padding-bottom: 4mm; border-bottom: 3px solid #000; margin-bottom: 6mm;
        }
        .ar-title { font-size: 22px; font-weight: 900; letter-spacing: -.02em; line-height: 1 }
        .ar-fecha { font-size: 13px; font-weight: 700; color: #555; margin-top: 2mm; letter-spacing: .05em }
        .ar-stats { display: flex; gap: 8mm; font-size: 11px; font-weight: 600; color: #555; text-transform: uppercase; letter-spacing: .1em }
        .ar-stat-num { display: block; font-size: 28px; font-weight: 900; color: #000; letter-spacing: -.04em; line-height: 1 }
        .ar-consol-title {
          font-size: 11px; font-weight: 800; letter-spacing: .15em;
          background: #000; color: #fff; padding: 2mm 3mm; margin-bottom: 3mm;
        }
        .ar-consol { width: 100%; border-collapse: collapse; font-size: 12px }
        .ar-consol th {
          font-size: 9px; font-weight: 800; letter-spacing: .1em; color: #555;
          text-transform: uppercase; text-align: left; padding: 1.5mm 2mm;
          border-bottom: 1.5px solid #000;
        }
        .ar-consol td { padding: 2mm; border-bottom: 1px solid #ddd; vertical-align: top }
        .ar-consol tr:nth-child(even) { background: #FAFAFA }

        /* PEDIDO */
        .ar-pedido {
          border: 2px solid #000; border-radius: 4px;
          margin-bottom: 8mm; padding: 4mm;
        }
        .ar-ped-head {
          display: grid; grid-template-columns: auto 1fr auto; gap: 4mm;
          align-items: center; padding-bottom: 3mm; border-bottom: 2px solid #000; margin-bottom: 3mm;
        }
        .ar-ped-num {
          background: #000; color: #fff; padding: 2mm 3mm; border-radius: 3px;
          font-size: 11px; font-weight: 800; letter-spacing: .08em; white-space: nowrap;
        }
        .ar-ped-nombre { font-size: 16px; font-weight: 900; text-transform: uppercase; letter-spacing: -.01em; line-height: 1.1 }
        .ar-ped-dir { font-size: 12px; margin-top: 1mm }
        .ar-ped-vivienda {
          display: inline-block; padding: 0 1.5mm; margin-left: 2mm;
          background: #000; color: #fff; font-size: 9px; font-weight: 800; letter-spacing: .1em; border-radius: 2px;
        }
        .ar-ped-meta { text-align: right }
        .ar-ped-bultos {
          font-size: 28px; font-weight: 900; line-height: 1; letter-spacing: -.04em;
        }
        .ar-ped-bultos span { display: block; font-size: 8px; font-weight: 800; letter-spacing: .15em; margin-top: 0.5mm }
        .ar-ped-ventana { font-size: 10px; font-weight: 700; margin-top: 1mm; letter-spacing: .05em }
        .ar-ped-orden { font-size: 9px; font-family: monospace; color: #666; margin-top: 0.5mm }

        .ar-notas {
          background: #fff; border: 1.5px dashed #000; border-radius: 2px;
          padding: 2mm 3mm; font-size: 11px; margin-bottom: 3mm;
        }

        .ar-ped-kit-name {
          font-size: 13px; font-weight: 800; letter-spacing: -.01em;
          padding: 2mm 0; margin-bottom: 1mm;
        }
        .ar-ped-table { width: 100%; border-collapse: collapse; font-size: 12px }
        .ar-ped-table td { padding: 1.5mm 2mm; border-bottom: 1px solid #eee; vertical-align: top }
        .ar-ped-table tr.ar-sep td {
          font-size: 10px; font-weight: 800; letter-spacing: .15em;
          padding: 2mm 2mm 1mm; border-bottom: 1.5px solid #000;
          color: #000;
        }
        .ar-c { width: 30px; text-align: center }
        .ar-q { width: 50px; text-align: right; font-weight: 800; font-size: 14px; font-variant-numeric: tabular-nums }
        .ar-n { padding-right: 2mm }
        .ar-prod { font-weight: 600 }
        .ar-pres { color: #666; font-size: 11px }
        .ar-s { width: 80px; font-family: monospace; font-size: 10px; color: #666 }

        .ar-check {
          display: inline-block; width: 16px; height: 16px;
          border: 1.5px solid #000; border-radius: 3px;
        }

        body { padding-top: 56px; }
      `}</style>
    </>
  )
}
